// src/services/backup.api.ts
import { apiFetch, API_BASE_URL, getCsrfToken } from "./apiClient";

const BASE = `${API_BASE_URL}/backup`;

/** Lo que el backend informa además del resumen de filas por tabla. */
export interface RestoreExtras {
  /** Tablas que venían en el ZIP y ya no existen en la base: no se insertaron. */
  skipped?: string[];
  /** Tablas que existían en la base y no venían en el ZIP: se vaciaron. */
  noIncluidas?: string[];
  /** Archivos del Volume (fotos, videos, documentos) escritos al disco. */
  filesRestored?: number;
  /** Archivos que no se pudieron escribir. `-1` = falló todo el bloque. */
  filesFailed?: number;
  filesErrors?: Array<{ entry: string; error: string }>;
  /**
   * `true` cuando el backup NO trae fotos, videos ni documentos (se descargó
   * con «Incluir fotos, videos y documentos» apagado, o es de la época «solo
   * tablas»). La base se restauró bien; los adjuntos que no estén ya en el
   * volumen quedan como referencias rotas. Un ZIP con medios responde `false`.
   */
  sinMedios?: boolean;
  /** Texto del backend que explica qué implica `sinMedios` (solo viene si es `true`). */
  advertenciaMedios?: string;
  /**
   * Lo que se escribió al volumen, por clase de archivo (los mismos conceptos que la
   * estimación y `BACKUP_INFO.json`). Un servidor anterior no lo manda.
   */
  desgloseArchivos?: DesgloseMedios;
  /**
   * Cuántos usuarios que ya existían tienen ahora OTRO rol después de restaurar o importar (el respaldo traía un rol distinto). Un servidor
   * anterior no lo manda.
   */
  usuariosCambiaronDeRol?: number;
  /** Cuántos superadministradores quedaron después de restaurar o importar (el servidor aborta si quedaría ninguno). */
  superadministradores?: number;
}

/** Cuántos archivos y cuánto pesan, de una clase de archivo del volumen. */
export interface ClaseMedios {
  archivos: number;
  bytes: number;
}

/**
 * Los archivos FÍSICOS del volumen separados por clase, según la carpeta donde viven:
 * `originales` (fotos, videos y documentos), `miniaturas`, `livianas` (las versiones
 * livianas de los videos) y `otros` (cualquier otra cosa que haya en el volumen).
 */
export interface DesgloseMedios {
  originales: ClaseMedios;
  miniaturas: ClaseMedios;
  livianas: ClaseMedios;
  otros: ClaseMedios;
}

/**
 * Qué llevaría el ZIP con medios (`GET /backup/estimacion`). Es el «dry run» del
 * export: el servidor recorre el volumen con el mismo inventario con el que arma el ZIP,
 * así que `archivosMedios` y `bytesMedios` son los archivos FÍSICOS que traerá (no los
 * «archivos» de /almacenamiento, que cuentan una foto con su miniatura como uno solo).
 */
export interface EstimacionMedios {
  bytesMedios: number;
  archivosMedios: number;
  /** Los mismos archivos separados en originales / miniaturas / versiones livianas / otros. */
  desglose?: DesgloseMedios;
  /** Espacio libre del volumen; `null` si el servidor no lo informa. */
  libreBytes: number | null;
  /** Cuándo se hizo el cálculo (ISO). */
  calculadoEn?: string | null;
}

export interface RestoreResult extends RestoreExtras {
  success: boolean;
  summary: Record<string, { restored: number }>;
}

/** Respuesta cruda: el 409 de confirmación necesita leerse, no colapsarse. */
export interface RespuestaCruda<T> {
  status: number;
  data: any;
  ok: boolean;
  result: T | null;
}

/**
 * POST multipart que NO colapsa el error.
 *
 * `apiFetch` devuelve `null` ante cualquier respuesta no-2xx (por diseño), y
 * acá el 409 de "el backup no incluye estas tablas" ES la información: trae la
 * lista que el operador tiene que mirar antes de decidir. Con `apiFetch` ese
 * detalle se convertía en un toast genérico.
 */
async function postArchivos<T>(
  path: string,
  files: File[],
  campos: Record<string, string> = {},
): Promise<RespuestaCruda<T>> {
  const form = new FormData();
  files.forEach((f) => form.append("files", f));
  Object.entries(campos).forEach(([k, v]) => form.append(k, v));

  const headers: Record<string, string> = {};
  const csrf = getCsrfToken();
  if (csrf) headers["X-CSRF-Token"] = csrf;

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    credentials: "include",
    headers,
    body: form,
  });
  const data = await res.json().catch(() => null);
  return { status: res.status, data, ok: res.ok, result: res.ok ? (data as T) : null };
}

/**
 * Lo que se le dice a la persona cuando restaurar o importar se rechaza por una de estas dos causas, según el CÓDIGO que manda el servidor. El servidor
 * ya manda un texto, pero un 5xx en producción lo reemplaza por «Internal server error»: con el código en mano el aviso sigue siendo claro. Ver
 * backend/src/services/botMantenimiento.js y backend/src/utils/backupUsuarios.js.
 */
export const MENSAJES_POR_CODIGO: Readonly<Record<string, string>> = {
  // 503: el asistente de WhatsApp estaba guardando algo y no terminó a tiempo; no se tocó nada.
  ESCRITURAS_DEL_BOT_EN_CURSO:
    "No se restauró nada: el asistente de WhatsApp estaba guardando algo y no terminó a tiempo. La base quedó como estaba. Vuelve a intentarlo en un minuto.",
  // 409: el respaldo dejaría al sistema sin ningún superadministrador; se revirtió todo.
  SIN_SUPERADMINISTRADOR:
    "No se aplicó nada: con este respaldo el sistema se quedaría sin ningún superadministrador. La base quedó como estaba. Revisa que el respaldo traiga al menos una cuenta de superadministrador.",
};

/** El código de error de cualquiera de los formatos del backend (en la raíz, o dentro de `error`). */
const codigoDeError = (data: any): string | null => {
  const codigo = data?.code ?? data?.error?.code;
  return typeof codigo === "string" ? codigo : null;
};

/** Saca un mensaje legible de cualquiera de los tres formatos de error del backend. */
export function mensajeDeError(data: any, porDefecto: string): string {
  const codigo = codigoDeError(data);
  if (codigo !== null && Object.prototype.hasOwnProperty.call(MENSAJES_POR_CODIGO, codigo)) return MENSAJES_POR_CODIGO[codigo];
  return (
    data?.message ||
    (typeof data?.error === "string" ? data.error : data?.error?.message) ||
    porDefecto
  );
}

/**
 * Descarga un blob desde una ruta de `/api/*`. No podemos usar `apiFetch`
 * porque éste parsea JSON; aquí necesitamos el cuerpo binario tal cual.
 * La auth viaja por cookie HttpOnly (`credentials: "include"` lo agrega
 * automáticamente el interceptor global de `window.fetch`). Round
 * 2026-04-26 fase 6 (P1-B): se eliminó el header `Authorization: Bearer`
 * para no depender del JWT en localStorage.
 */
async function fetchBlob(url: string, errorMsg: string): Promise<Blob> {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(errorMsg);
  return resp.blob();
}

export const backupApi = {
  /** List available tables */
  getTables: async (): Promise<string[]> => {
    const res = await apiFetch<{ data: string[] }>("/backup/tables");
    if (!res) throw new Error("Error al listar tablas");
    return res.data;
  },

  /**
   * Peso estimado de los medios, o `null` si no se pudo estimar. Es información
   * opcional (la descarga funciona igual), así que va `silent`: sin toast de
   * error, y la página muestra «no disponible».
   */
  getEstimacion: async (): Promise<EstimacionMedios | null> => {
    const res = await apiFetch<{ data: EstimacionMedios }>("/backup/estimacion", {
      silent: true,
    });
    return res?.data ?? null;
  },

  /**
   * Download full ZIP backup — returns blob.
   *
   * `incluirMedios` viaja SIEMPRE explícito (`medios=1|0`): así la elección del
   * operador no depende del default del servidor.
   */
  exportAll: (incluirMedios = true): Promise<Blob> =>
    fetchBlob(
      `${BASE}/export?medios=${incluirMedios ? "1" : "0"}`,
      "Error al exportar backup",
    ),

  /** Download single table CSV — returns blob */
  exportTable: (tabla: string): Promise<Blob> =>
    fetchBlob(`${BASE}/export/${tabla}`, "Error al exportar tabla"),

  /** Incremental import */
  importIncremental: async (
    files: File[],
  ): Promise<{
    success: boolean;
    summary: Record<string, { imported: number }>;
    skipped?: string[];
    usuariosCambiaronDeRol?: number;
    superadministradores?: number;
  }> => {
    const res = await postArchivos<{
      success: boolean;
      summary: Record<string, { imported: number }>;
      skipped?: string[];
      usuariosCambiaronDeRol?: number;
      superadministradores?: number;
    }>("/backup/import", files);
    if (!res.result) throw new Error(mensajeDeError(res.data, "Error al importar"));
    return res.result;
  },

  /**
   * Restauración completa. Devuelve la respuesta CRUDA porque el 409 de
   * confirmación (`TABLAS_NO_INCLUIDAS`) no es un error a mostrar y olvidar:
   * el hook tiene que leer la lista, preguntarle al operador y reintentar.
   */
  restoreFull: (files: File[], confirmarNoIncluidas = false) =>
    postArchivos<RestoreResult>(
      "/backup/restore",
      files,
      confirmarNoIncluidas ? { confirmarNoIncluidas: "true" } : {},
    ),
};
